import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Plus, Calendar as CalendarIcon, Users, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const STATUS_LABEL = { planning: "Planejamento", in_progress: "Em execução", completed: "Concluído", archived: "Arquivado" };
const STATUS_COLOR = { planning: "#71717A", in_progress: "#3B82F6", completed: "#10B981", archived: "#F59E0B" };

export default function Events() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", client_name: "", kind: "corporativo", starts_at: "", location: "", audience_size: 0 });
  const navigate = useNavigate();
  const load = () => api.get("/events").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title) return toast.error("Título obrigatório");
    const r = await api.post("/events", { ...form, audience_size: +form.audience_size });
    toast.success("Evento criado");
    setOpen(false);
    navigate(`/events/${r.data.event_id}`);
  };

  return (
    <div className="p-10 max-w-[1400px]">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="overline">Produção & Execução</p>
          <h1 className="font-heading text-4xl mt-2">Eventos Corporativos</h1>
          <p className="text-sm text-muted-foreground mt-2">Do checklist ao post-event report — tudo em um só lugar.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#C07C88] hover:bg-[#A86571]" data-testid="new-event-btn"><Plus size={16} className="mr-1" /> Novo evento</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Novo evento</DialogTitle><DialogDescription>Preencha os dados básicos. Checklist operacional e participantes podem ser configurados em seguida.</DialogDescription></DialogHeader>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><Label>Título</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title" /></div>
              <div><Label>Cliente</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="event-client" /></div>
              <div>
                <Label>Tipo</Label>
                <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="w-full h-10 px-3 border border-input rounded-md text-sm bg-white" data-testid="event-kind">
                  <option value="corporativo">Corporativo</option>
                  <option value="palestra">Palestra</option>
                  <option value="workshop">Workshop</option>
                  <option value="congresso">Congresso</option>
                </select>
              </div>
              <div><Label>Data</Label><Input type="date" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="event-date" /></div>
              <div><Label>Vagas</Label><Input type="number" value={form.audience_size} onChange={(e) => setForm({ ...form, audience_size: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Local</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
            </div>
            <DialogFooter><Button className="bg-[#C07C88] hover:bg-[#A86571]" onClick={create} data-testid="create-event-btn">Criar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <Card className="p-16 border-border shadow-none border-dashed text-center">
          <CalendarIcon size={40} strokeWidth={1.3} className="mx-auto text-muted-foreground" />
          <p className="font-heading text-2xl mt-4">Sem eventos ainda</p>
          <p className="text-sm text-muted-foreground mt-2">Crie seu primeiro evento e organize checklist, fornecedores e crachás em minutos.</p>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((e) => (
            <Link to={`/events/${e.event_id}`} key={e.event_id} data-testid={`event-card-${e.event_id}`}>
              <Card className="p-6 border-border shadow-none hover:-translate-y-0.5 hover:shadow-sm transition-all bg-white">
                <div className="flex items-start justify-between">
                  <p className="font-mono text-[10px] text-muted-foreground">{e.event_id.slice(0, 12)}</p>
                  <span className="inline-flex items-center text-[10px] font-medium">
                    <span className="status-dot" style={{ background: STATUS_COLOR[e.status] }} /> {STATUS_LABEL[e.status]}
                  </span>
                </div>
                <p className="font-heading text-lg mt-3 line-clamp-2">{e.title}</p>
                <p className="text-xs text-muted-foreground mt-1">{e.client_name || "—"}</p>
                <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Users size={12} /> {e.participants?.length || 0}</span>
                  <span className="uppercase tracking-wider text-[9px]">{e.kind}</span>
                  {e.starts_at && <span>{e.starts_at.slice(0, 10)}</span>}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
